import { Div, Text } from '@sendoutcards/quantum-design-ui'
import React from 'react'
import {
  CardWithoutCostFragment,
  DigitalCardOrderDataItemInnerGiftCardRunaSingle,
  MinimalCardPreviewFragment,
  MinimalDigitalCardFragment,
} from 'src/graphql/generated/graphql'
import styles from './digitalCardHistoryCell.module.scss'
import { Button } from 'src/design_system/components/Button/Button'
import { useDeviceInfo, useState } from 'src/hooks'
import { LEGACY_SITE_URL } from 'src/app/api'
import { useDigitalGiftCardProduct, useMarketingContent } from 'src/react_query'
import { getCreateActSharePayload } from 'src/utils/getCreateActSharePayload'
import { ToolTip } from 'src/design_system/molecules/toolip/ToolTip'

interface DigitalCardHistoryCellProps {
  digitalCard: MinimalDigitalCardFragment
  onViewCard: (card: MinimalCardPreviewFragment) => void
  giftItem?: DigitalCardOrderDataItemInnerGiftCardRunaSingle
}

const baseURL = LEGACY_SITE_URL

const DigitalCardHistoryCell: React.FunctionComponent<
  DigitalCardHistoryCellProps
> = props => {
  const { digitalCard, onViewCard, giftItem } = props

  const { data: giftCardDetails } = useDigitalGiftCardProduct({
    code: giftItem?.code ?? '',
  })

  const deviceInfo = useDeviceInfo()
  const { data: marketingContent } = useMarketingContent()
  const [copyBtTitle, setCopyBtTitle] = useState('Copy Link')
  const [isHovering, setIsHovering] = useState(false)

  const createdAtDateFormatted = new Date(digitalCard.createdAt)

  const fontFamily = 'Montserrat, sans-serif'

  const defaultActShareMessage = giftCardDetails
    ? (marketingContent?.defaultActGiftCardDefaultMessage.content ?? '')
    : (marketingContent?.defaultActShareMessage.content ?? '')

  const token = digitalCard.recipients[0].token
  const link = `${baseURL}/act/${token}`

  const shareData = getCreateActSharePayload(
    deviceInfo.os,
    link,
    defaultActShareMessage,
  )

  const giftCardToolTip = (
    <ToolTip
      isVisible={isHovering}
      direction="bottom"
      top="-63px"
      left={digitalCard.card.isHorizontal ? '75px' : '43px'}
    >
      <div className={styles.giftCardToolTipContainer}>
        <p
          style={{
            fontFamily: fontFamily,
            fontSize: '12px',
            color: '#FFF',
            whiteSpace: 'nowrap',
            fontWeight: 700,
            letterSpacing: '.02rem',
            lineHeight: '1.2rem',
          }}
        >
          Attached Gift Card:
        </p>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className={styles.detailsContainer}>
            <p className={styles.detailLabel}>Brand:</p>
            <p className={styles.detail}>{giftCardDetails?.title}</p>
          </div>
          <div className={styles.detailsContainer}>
            <p className={styles.detailLabel}>Amount</p>
            <p className={styles.detail}>{giftItem?.faceValue}</p>
          </div>
          <div className={styles.detailsContainer}>
            <p className={styles.detailLabel}>Currency:</p>
            <p className={styles.detail}>{giftItem?.currency}</p>
          </div>
        </div>
      </div>
    </ToolTip>
  )

  return (
    <div className={styles.digitalCardHistoryRowWrapper}>
      <div className={styles.dchPreviewAndDetailsWrapper}>
        <div className={styles.cardContainer}>
          <div
            className={styles.dchFrontImage}
            style={{
              backgroundImage: `url("${digitalCard.card.frontPreviewUrl}")`,
              cursor: 'pointer',
              position: 'relative',
            }}
            onClick={() =>
              onViewCard(digitalCard.card as unknown as CardWithoutCostFragment)
            }
          >
            {giftItem && (
              <>
                {giftCardToolTip}
                <Div
                  className={styles.giftCardDisplay}
                  onHoverStart={() => setIsHovering(true)}
                  onHoverEnd={() => setIsHovering(false)}
                  style={{
                    backgroundImage: `url(${giftCardDetails?.image})`,
                    bottom: '3px',
                    right: digitalCard.card.isHorizontal ? '-9px' : '15px',
                  }}
                />
              </>
            )}
          </div>
          <p className={styles.token}>{token}</p>
        </div>
        <div>
          <div className={styles.dchTitle}>
            <Text type="body" weight="bold">
              {digitalCard.shareMessageTitle}
            </Text>
          </div>
          <div className={styles.dchCreatedOn}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Text
                type="footnote"
                isItalic={true}
                weight="thin"
                outset={{ right: 'x1' }}
              >
                Created On:
              </Text>
              <Text type="caption">
                {createdAtDateFormatted.toLocaleDateString('en-US')}
              </Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Text
                type="footnote"
                isItalic={true}
                weight="thin"
                content="Status:"
                outset={{ right: 'x1' }}
              />
              <Text type="caption" style={{ fontWeight: 600 }}>
                {digitalCard.recipients[0].shareStatus ?? 'Unknown'}
              </Text>
            </div>
          </div>
          <div className={styles.dchContact}>
            <Text
              type="footnote"
              isItalic={true}
              weight="thin"
              outset={{ right: 'x1' }}
            >
              Sent to:
            </Text>
            <Text type="caption">
              {`${digitalCard.recipients[0].contactRequest?.contact?.firstName ?? digitalCard.recipients[0].contactRequest?.firstName ?? ''} ${digitalCard.recipients[0].contactRequest?.contact?.lastName ?? digitalCard.recipients[0].contactRequest?.lastName ?? ''}`}
            </Text>
          </div>
        </div>
      </div>
      <div className={styles.dchShareLink}>
        <Button
          title={copyBtTitle}
          width="100%"
          whiteSpace="nowrap"
          background="#000"
          textColor="#fff"
          onClick={async () => {
            await navigator.clipboard.writeText(`${link}`)
            setCopyBtTitle('Link Copied')
          }}
        />
        {typeof navigator !== 'undefined' &&
          typeof navigator.canShare === 'function' &&
          navigator.canShare?.(shareData) && (
            <Button
              title={'Share'}
              width="100%"
              onClick={async () => {
                if (navigator?.share && navigator.canShare(shareData)) {
                  try {
                    await navigator.share(shareData)
                  } catch (err) {
                    console.error(err)
                  }
                }
              }}
            />
          )}
      </div>
    </div>
  )
}

export default DigitalCardHistoryCell
